// Streaming client for the PUBLIC marketing concierge (Aria). Anonymous — no auth, no cookies (the
// marketing site holds no session), so no `credentials`. Hits /v1/public/concierge and yields parsed
// SSE events. Mirrors the patient portal's client, credential-free.
import { apiOrigin } from "../config";

export type ConciergeRole = "user" | "assistant";
export interface ConciergeMessage {
  role: ConciergeRole;
  content: string;
}
/** chunk → append; refusal → discard the partial and show this; error → show this and stop. */
export type ConciergeEvent =
  | { type: "chunk"; text: string }
  | { type: "refusal"; text: string }
  | { type: "error"; text: string };

export async function* conciergeStream(
  messages: ConciergeMessage[],
): AsyncGenerator<ConciergeEvent> {
  const res = await fetch(`${apiOrigin()}/v1/public/concierge`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
    body: JSON.stringify({ messages }),
  });

  if (!res.ok || !res.body) {
    const text =
      res.status === 429
        ? "You're sending messages a little fast — give me a moment."
        : "Sorry — I couldn't reach Aria just now. Please try again.";
    yield { type: "error", text };
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let sep: number;
    while ((sep = buf.indexOf("\n\n")) !== -1) {
      const frame = buf.slice(0, sep);
      buf = buf.slice(sep + 2);
      let event = "message";
      let data = "";
      for (const line of frame.split("\n")) {
        if (line.startsWith("event:")) event = line.slice(6).trim();
        else if (line.startsWith("data:")) data += line.slice(5).trim();
      }
      if (event === "done") return;
      let text = "";
      try {
        text = (JSON.parse(data) as { text?: string }).text ?? "";
      } catch {
        /* ignore malformed frame */
      }
      if (event === "chunk") yield { type: "chunk", text };
      else if (event === "refusal") yield { type: "refusal", text };
      else if (event === "error") yield { type: "error", text };
    }
  }
}
